import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestDetails } from "../../../../api/types/dataCollectionTypes";

interface Props {
  dataRequestId: string;
  submissionIds: string[];
  recipientNames: string[];
  open: boolean;
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const deleteDataRequestSubmissions = withErrorHandling(adminApi.deleteDataRequestSubmissions);

const DeleteSubmissionsDialog = ({ dataRequestId, submissionIds, recipientNames, open, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const handleApply = async () => {
    setIsSaving(true);
    const [dataRequest, error] = await deleteDataRequestSubmissions(dataRequestId, { submissionIds });
    setIsSaving(false);

    if (error) {
      logError(error, "[DeleteSubmissionsDialog] deleteDataRequestSubmissions");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Deleted successfully");
    onApply(dataRequest);
  };

  const prompt =
    recipientNames.length > 1
      ? `Delete ${recipientNames.length} submissions?`
      : `Delete submission for ${recipientNames[0]}?`;

  const descriptionPart1 =
    recipientNames.length > 1
      ? `You're about to delete ${recipientNames.length} submissions.`
      : `You're about to delete the submission.`;

  const description = `${descriptionPart1} ${submissionIds.length > 1 ? "The submissions" : "It"} will be permanently deleted and will not be published on the portal.`;

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {prompt}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={isSaving} onClick={handleApply}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteSubmissionsDialog;
